import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  }
});

// Export keys for direct API calls if needed
export { supabaseUrl, supabaseAnonKey };

export interface Property {
  id: string;
  user_id: string;
  address: string;
  recommendations: any[];
  zillow_data: any;
  created_at: string;
  updated_at: string;
}

export interface ChatMessage {
  id: string;
  property_id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

// Return early for better error handling rather than chaining
export const saveProperty = async (address: string, recommendations: string[]) => {
  const user = await supabase.auth.getUser();
  if (!user.data.user?.id) {
    throw new Error('User not authenticated');
  }

  const { data: property, error } = await supabase
    .from('properties')
    .insert({
      address,
      recommendations,
      user_id: user.data.user.id
    })
    .select()
    .single();

  if (error) throw error;
  return property;
};

export const savePropertyWithZillow = async (address: string, zillowResponse: any) => {
  const user = await supabase.auth.getUser();
  if (!user.data.user?.id) {
    throw new Error('User not authenticated');
  }

  const userId = user.data.user.id;

  // Basic property data
  const propertyData = {
    address,
    user_id: userId,
    recommendations: [],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    zillow_data: zillowResponse  // Store the entire response
  };

  // Check if property exists
  const { data: existingProperties, error: searchError } = await supabase
    .from('properties')
    .select('id')
    .eq('address', address)
    .eq('user_id', userId);

  if (searchError) {
    console.error('Error searching for existing property:', searchError);
    throw searchError;
  }

  // Update or insert
  if (existingProperties && existingProperties.length > 0) {
    const { data: updatedProperty, error: updateError } = await supabase
      .from('properties')
      .update(propertyData)
      .eq('id', existingProperties[0].id)
      .select()
      .single();

    if (updateError) {
      throw updateError;
    }

    return updatedProperty;
  } else {
    const { data: newProperty, error: insertError } = await supabase
      .from('properties')
      .insert(propertyData)
      .select()
      .single();

    if (insertError) {
      throw insertError;
    }

    return newProperty;
  }
};

export const getProperty = async (address: string) => {
  const user = await supabase.auth.getUser();
  if (!user.data.user?.id) {
    throw new Error('User not authenticated');
  }

  const { data: property, error } = await supabase
    .from('properties')
    .select('*, chat_messages(*)')
    .eq('address', address)
    .eq('user_id', user.data.user.id)
    .maybeSingle();

  if (error) throw error;
  return property;
};

export const updateRecommendations = async (propertyId: string, recommendations: string[]) => {
  const { error } = await supabase
    .from('properties')
    .update({
      recommendations,
      updated_at: new Date().toISOString()
    })
    .eq('id', propertyId);

  if (error) throw error;
};

export const updatePropertyZillowData = async (propertyId: string, zillowData: any) => {
  const { error } = await supabase
    .from('properties')
    .update({
      zillow_data: zillowData,
      updated_at: new Date().toISOString()
    })
    .eq('id', propertyId);

  if (error) throw error;
};

export const addChatMessage = async (propertyId: string, role: 'user' | 'assistant', content: string) => {
  const { data: message, error } = await supabase
    .from('chat_messages')
    .insert({
      property_id: propertyId,
      role,
      content
    })
    .select()
    .single();

  if (error) throw error;
  return message;
};

export const getUserProperties = async () => {
  const user = await supabase.auth.getUser();
  if (!user.data.user?.id) {
    throw new Error('User not authenticated');
  }

  const { data: properties, error } = await supabase
    .from('properties')
    .select(`
      *,
      chat_messages (
        *
      )
    `)
    .eq('user_id', user.data.user.id)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return properties;
};

export const getSavedProperties = async () => {
  const user = await supabase.auth.getUser();
  if (!user.data.user?.id) {
    throw new Error('User not authenticated');
  }

  const { data: properties, error } = await supabase
    .from('properties')
    .select('*')
    .eq('user_id', user.data.user.id)
    .order('created_at', { ascending: false });

  if (error) {
    throw error;
  }

  return properties as Property[];
};

export const deleteProperty = async (propertyId: string) => {
  const user = await supabase.auth.getUser();
  if (!user.data.user?.id) {
    throw new Error('User not authenticated');
  }

  // First delete related chat messages
  const { error: chatError } = await supabase
    .from('chat_messages')
    .delete()
    .eq('property_id', propertyId);

  if (chatError) {
    throw chatError;
  }

  // Then delete the property
  const { error } = await supabase
    .from('properties')
    .delete()
    .eq('id', propertyId)
    .eq('user_id', user.data.user.id);

  if (error) {
    throw error;
  }
};
